import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Activity, BarChart2, Cpu, TrendingUp, Sun } from 'lucide-react';
import { useAuth, roleHomePaths } from '../../context/AuthContext';
import { UserRole } from '../../types';
import AvionLogo from '../../components/shared/AvionLogo';

type LoginStep = 'credentials' | 'totp';
interface MeResponse {
  id: string; username: string; displayName: string;
  role: string; org: string; scope: string;
}

const FEATURES = [
  { icon: <Activity size={14} />,   title: 'Real-Time Monitoring',  desc: 'Live inverter telemetry with 15-min granularity across every installation.' },
  { icon: <BarChart2 size={14} />,  title: '100+ Smart KPIs',        desc: 'Generation, PR, CUF, specific yield and grid metrics at every level.' },
  { icon: <Cpu size={14} />,        title: 'Intelligent Alerts',     desc: 'Fault detection with plain-language diagnostics and AI-guided resolution.' },
  { icon: <TrendingUp size={14} />, title: 'AI-Driven Predictions',  desc: 'Forecasted generation, underperformance detection and portfolio benchmarking.' },
];

const STAKEHOLDERS = [
  'National Level', 'State Agencies', 'Renewable Energy Bodies',
  'DISCOMs', 'Vendors & EPC Partners', 'Consumers',
];

/* Solar panel cell style — same as the SVG panels on the right:
   diagonal gradient base + 4-column × 3-row cell grid lines            */
const PANEL_BG   = 'linear-gradient(135deg, rgba(36,114,200,0.45) 0%, rgba(13,35,71,0.92) 100%)';
const PANEL_GRID = [
  PANEL_BG,
  'linear-gradient(rgba(36,114,200,0.28) 1px, transparent 1px)',   // horizontal cell lines
  'linear-gradient(90deg, rgba(36,114,200,0.28) 1px, transparent 1px)', // vertical cell lines
].join(', ');

/* ══════════════════════════════════════════════════════════════════════════
   LEFT PANEL — horizontal flow animation (background only)
   ══════════════════════════════════════════════════════════════════════════ */
function LeftPanelFlow() {
  const rows = [155, 230, 315, 400, 480, 545];
  return (
    <svg viewBox="0 0 680 700" className="absolute inset-0 w-full h-full pointer-events-none select-none" aria-hidden>
      <defs>
        <filter id="lp-glow">
          <feGaussianBlur stdDeviation="2.5" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {rows.map((y, i) => (
        <g key={i}>
          {/* Dashed flow line */}
          <line x1="0" y1={y} x2="680" y2={y}
            stroke="#2472C8"
            strokeWidth={i % 2 === 0 ? 1.1 : 0.65}
            opacity={i % 2 === 0 ? 0.16 : 0.09}
            strokeDasharray="10 7">
            <animate attributeName="stroke-dashoffset"
              from="0" to="-34" dur={`${1.3 + i * 0.14}s`} repeatCount="indefinite" />
          </line>
          {/* Primary particle */}
          <circle r="3.5" fill="#2472C8" opacity="0" filter="url(#lp-glow)">
            <animateMotion path={`M -20 ${y} L 700 ${y}`}
              dur={`${4.2 + i * 0.42}s`} begin={`${i * 0.55}s`} repeatCount="indefinite" />
            <animate attributeName="opacity"
              values="0;0.85;0.85;0" keyTimes="0;0.07;0.93;1"
              dur={`${4.2 + i * 0.42}s`} begin={`${i * 0.55}s`} repeatCount="indefinite" />
          </circle>
          {/* Trailing particle */}
          <circle r="2" fill="#2472C8" opacity="0" filter="url(#lp-glow)">
            <animateMotion path={`M -20 ${y} L 700 ${y}`}
              dur={`${4.2 + i * 0.42}s`} begin={`${i * 0.55 + 2.1}s`} repeatCount="indefinite" />
            <animate attributeName="opacity"
              values="0;0.5;0.5;0" keyTimes="0;0.1;0.9;1"
              dur={`${4.2 + i * 0.42}s`} begin={`${i * 0.55 + 2.1}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   RIGHT PANEL — original solar animation (panels at bottom → energy flows UP)
   ══════════════════════════════════════════════════════════════════════════ */
function SolarAnimation() {
  const panelPositions = [
    [30, 250], [155, 250], [280, 250],
    [30, 340], [155, 340], [280, 340],
  ];
  const busY  = 200;
  const sunCX = 200;
  const sunCY = 72;

  return (
    <svg viewBox="0 0 400 560" className="absolute inset-0 w-full h-full pointer-events-none select-none"
      xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <filter id="rp-glow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="3.5" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <linearGradient id="panelFill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor="#2472C8" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#0d2347" stopOpacity="0.25" />
        </linearGradient>
        <linearGradient id="curveGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#2472C8" stopOpacity="0" />
          <stop offset="40%"  stopColor="#2472C8" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#2472C8" stopOpacity="0.2" />
        </linearGradient>
      </defs>

      {/* ── Sun ── */}
      <g transform={`translate(${sunCX},${sunCY})`} filter="url(#rp-glow)">
        <circle r="52" fill="none" stroke="#2472C8" strokeWidth="0.6" strokeDasharray="5 5" opacity="0.22">
          <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="30s" repeatCount="indefinite" />
        </circle>
        <circle r="37" fill="none" stroke="#2472C8" strokeWidth="0.9" strokeDasharray="3 3" opacity="0.3">
          <animateTransform attributeName="transform" type="rotate" from="360" to="0" dur="18s" repeatCount="indefinite" />
        </circle>
        <circle r="23" fill="#2472C8" opacity="0.14" />
        <circle r="15" fill="#2472C8" opacity="0.38" />
        <circle r="9"  fill="#2472C8" opacity="0.72" />
        {[0,45,90,135,180,225,270,315].map(a => {
          const r = (a * Math.PI) / 180;
          return <line key={a}
            x1={Math.cos(r)*20} y1={Math.sin(r)*20}
            x2={Math.cos(r)*34} y2={Math.sin(r)*34}
            stroke="#2472C8" strokeWidth="1.6" strokeLinecap="round" opacity="0.38" />;
        })}
        <circle r="9" fill="none" stroke="#2472C8" strokeWidth="2" opacity="0">
          <animate attributeName="r"       values="9;30;9"   dur="3s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.6;0;0.6" dur="3s" repeatCount="indefinite" />
        </circle>
      </g>

      {/* ── Sun → bus vertical feeder ── */}
      <line x1={sunCX} y1={sunCY + 18} x2={sunCX} y2={busY}
        stroke="#2472C8" strokeWidth="1.4" opacity="0.35" strokeDasharray="5 4">
        <animate attributeName="stroke-dashoffset" from="0" to="-18" dur="0.9s" repeatCount="indefinite" />
      </line>
      {[0, 0.5].map(d => (
        <circle key={d} r="3.5" fill="#2472C8" opacity="0.9" filter="url(#rp-glow)">
          <animateMotion path={`M ${sunCX} ${busY} L ${sunCX} ${sunCY + 18}`} dur="1.1s" begin={`${d}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0;0.9;0" dur="1.1s" begin={`${d}s`} repeatCount="indefinite" />
        </circle>
      ))}

      {/* ── Horizontal bus bar ── */}
      <line x1="15" y1={busY} x2="385" y2={busY} stroke="#2472C8" strokeWidth="2" opacity="0.32" />
      <circle cx={sunCX} cy={busY} r="7" fill="#2472C8" opacity="0.55" filter="url(#rp-glow)">
        <animate attributeName="r"       values="7;11;7"      dur="2.2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.55;0.85;0.55" dur="2.2s" repeatCount="indefinite" />
      </circle>
      <circle cx="23"  cy={busY} r="3.5" fill="#2472C8" opacity="0.28" />
      <circle cx="377" cy={busY} r="3.5" fill="#2472C8" opacity="0.28" />

      {/* ── Panels → bus (vertical, flowing UP) ── */}
      {panelPositions.map(([px, py], i) => {
        const cx  = px + 55;
        const dur = 1.4 + (i % 3) * 0.22;
        const del = i * 0.18;
        return (
          <g key={i}>
            <path d={`M ${cx} ${py} L ${cx} ${busY}`}
              fill="none" stroke="#2472C8" strokeWidth="1.1" opacity="0.28"
              strokeDasharray="5 4">
              <animate attributeName="stroke-dashoffset"
                from="0" to="-18" dur={`${dur}s`} begin={`${del}s`} repeatCount="indefinite" />
            </path>
            <circle r="2.5" fill="#2472C8" opacity="0.8" filter="url(#rp-glow)">
              <animateMotion path={`M ${cx} ${py} L ${cx} ${busY}`}
                dur={`${dur * 0.85}s`} begin={`${del}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values="0;0.85;0"
                dur={`${dur * 0.85}s`} begin={`${del}s`} repeatCount="indefinite" />
            </circle>
          </g>
        );
      })}

      {/* ── Solar panels ── */}
      {panelPositions.map(([x, y], i) => (
        <g key={i} transform={`translate(${x},${y})`}>
          <rect width="110" height="68" rx="3" fill="url(#panelFill)" stroke="#2472C8" strokeWidth="0.8" opacity="0.68" />
          {[1,2,3].map(c => <line key={c} x1={c*27.5} y1="0" x2={c*27.5} y2="68" stroke="#2472C8" strokeWidth="0.4" opacity="0.38" />)}
          {[1,2].map(r => <line key={r} x1="0" y1={r*22.7} x2="110" y2={r*22.7} stroke="#2472C8" strokeWidth="0.4" opacity="0.38" />)}
          {/* Sheen sweep */}
          <rect x="-120" y="0" width="50" height="68" rx="3" fill="white" opacity="0.07">
            <animateTransform attributeName="transform" type="translate"
              from="-110 0" to="200 0"
              dur={`${2.6 + i * 0.38}s`} begin={`${i * 0.48}s`} repeatCount="indefinite" />
          </rect>
          <circle cx="55" cy="0" r="3" fill="#2472C8" opacity="0.55" />
        </g>
      ))}

      {/* ── Generation power curve ── */}
      <g transform="translate(12,448)">
        <text x="0" y="0" fill="#2472C8" fontSize="8" opacity="0.4" fontFamily="system-ui">Generation Today</text>
        <path d="M0,82 C18,82 32,70 52,54 S86,20 112,9 S150,3 168,7 S208,28 232,52 S262,78 284,82 L390,82"
          fill="none" stroke="url(#curveGrad)" strokeWidth="2.2" opacity="0.72"
          strokeDasharray="600">
          <animate attributeName="stroke-dashoffset" values="600;0;600" dur="5s" repeatCount="indefinite" />
        </path>
        <path d="M0,82 C18,82 32,70 52,54 S86,20 112,9 S150,3 168,7 S208,28 232,52 S262,78 284,82 L390,82"
          fill="none" stroke="#2472C8" strokeWidth="7" opacity="0.06"
          strokeDasharray="600">
          <animate attributeName="stroke-dashoffset" values="600;0;600" dur="5s" repeatCount="indefinite" />
        </path>
        <circle cx="168" cy="7" r="4.5" fill="#2472C8" opacity="0.8" filter="url(#rp-glow)">
          <animate attributeName="opacity" values="0.8;0.3;0.8" dur="2s" repeatCount="indefinite" />
        </circle>
        <line x1="168" y1="7" x2="168" y2="82" stroke="#2472C8" strokeWidth="0.8" strokeDasharray="3 2" opacity="0.25" />
        <line x1="0" y1="82" x2="390" y2="82" stroke="#2472C8" strokeWidth="0.5" opacity="0.18" />
        <text x="158" y="98"  fill="#2472C8" fontSize="7" opacity="0.38" fontFamily="system-ui">Peak 12 PM</text>
        <text x="292" y="76"  fill="#2472C8" fontSize="9" opacity="0.52" fontFamily="system-ui" fontWeight="bold">36.4 GWh</text>
      </g>
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════════════ */
export default function LoginPage() {
  const navigate        = useNavigate();
  const { setAuthUser } = useAuth();

  const [loginStep, setLoginStep] = useState<LoginStep>('credentials');
  const [username,  setUsername]  = useState('');
  const [password,  setPassword]  = useState('');
  const [showPwd,   setShowPwd]   = useState(false);
  const [totp,      setTotp]      = useState(['','','','','','']);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');

  const totpRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (totp.every(d => d !== '')) handleTotpSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totp]);

  const handleCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!username.trim() || !password) { setError('Username and password are required.'); return; }
    setLoading(true);
    try {
      await axios.post('/api/login', { username: username.trim().toLowerCase(), password });
      setLoginStep('totp');
      setTimeout(() => totpRefs.current[0]?.focus(), 100);
    } catch (err: unknown) {
      setError(axios.isAxiosError(err) ? (err.response?.data?.error || 'Invalid credentials.') : 'Login failed.');
    } finally { setLoading(false); }
  };

  const handleTotpSubmit = async () => {
    const code = totp.join('');
    if (code.length < 6) return;
    setError(''); setLoading(true);
    try {
      const res = await axios.post<{ success: boolean; user: MeResponse }>('/api/login/totp', { token: code });
      setAuthUser(res.data.user);
      // Map server role string → UserRole enum → correct home path
      const serverRoleMap: Record<string, UserRole> = {
        admin:    UserRole.NATIONAL,
        national: UserRole.NATIONAL,
        state:    UserRole.STATE,
        discom:   UserRole.DISCOM,
        consumer: UserRole.CONSUMER,
        vendor:   UserRole.VENDOR,
      };
      const mappedRole = serverRoleMap[res.data.user.role] ?? UserRole.NATIONAL;
      navigate(roleHomePaths[mappedRole]);
    } catch (err: unknown) {
      setError(axios.isAxiosError(err) ? (err.response?.data?.error || 'Invalid code.') : 'Verification failed.');
      setTotp(['','','','','','']);
      setTimeout(() => totpRefs.current[0]?.focus(), 50);
    } finally { setLoading(false); }
  };

  const handleTotpChange = (idx: number, val: string) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    const next  = [...totp]; next[idx] = digit; setTotp(next);
    if (digit && idx < 5) totpRefs.current[idx + 1]?.focus();
  };
  const handleTotpKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !totp[idx] && idx > 0) totpRefs.current[idx - 1]?.focus();
  };
  const handleTotpPaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    setTotp(text.split('').concat(Array(6).fill('')).slice(0, 6));
    e.preventDefault();
  };

  return (
    <div className="min-h-screen flex">

      {/* ════════════════════════════════════════════════════════════════════
          LEFT PANEL
          ════════════════════════════════════════════════════════════════════ */}
      <div className="hidden lg:flex lg:w-[58%] bg-navy flex-col p-10 relative overflow-hidden">

        {/* Dot grid */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        {/* Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0d1e35]/60 via-navy to-[#0a1628] pointer-events-none" />
        {/* Flow animation */}
        <LeftPanelFlow />

        {/* ══ ZONE 1 — TOP: branding ══ */}
        <div className="relative z-10 flex items-center">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-accent flex items-center justify-center flex-shrink-0">
              <Sun size={20} className="text-white" />
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-white font-bold text-xl leading-tight">SMART™</span>
              <span className="text-white/55 text-sm">Platform</span>
              <span className="text-white/55 text-sm">by</span>
              <span className="text-accent font-bold text-sm">Avion</span>
            </div>
          </div>
        </div>

        {/* ══ ZONE 2 — CENTRE: SMART tagline + KPI cards (vertically centred) ══ */}
        <div className="relative z-10 flex-1 flex flex-col justify-center gap-5">

          {/* SMART full form */}
          <p className="text-lg font-light leading-relaxed" style={{ color: 'rgba(255,255,255,0.82)' }}>
            <span className="text-accent font-extrabold text-[1.4rem]">S</span>olar{' '}
            <span className="text-accent font-extrabold text-[1.4rem]">M</span>onitoring,{' '}
            <span className="text-accent font-extrabold text-[1.4rem]">A</span>nalytics &amp;{' '}
            <span className="text-accent font-extrabold text-[1.4rem]">R</span>emote{' '}
            <span className="text-accent font-extrabold text-[1.4rem]">T</span>elemetry™
          </p>

          {/* Feature cards — solar panel cell design matching right-side SVG panels */}
          <div className="grid grid-cols-2 gap-2.5">
            {FEATURES.map((f, i) => (
              <div
                key={i}
                className="flex flex-col justify-between p-3 rounded-xl transition-all duration-200 hover:brightness-110 overflow-hidden"
                style={{
                  backgroundImage: PANEL_GRID,
                  backgroundSize:  '100% 100%, 100% 33.33%, 25% 100%',
                  border:          '1px solid rgba(36,114,200,0.55)',
                }}
              >
                {/* Icon row */}
                <div className="flex items-start justify-between mb-2">
                  <div
                    className="w-7 h-7 rounded-md flex items-center justify-center text-accent flex-shrink-0"
                    style={{ background: 'rgba(36,114,200,0.25)', border: '1px solid rgba(36,114,200,0.40)' }}
                  >
                    {f.icon}
                  </div>
                  {/* Corner connection dot — mimics panel bus node */}
                  <div className="w-2 h-2 rounded-full opacity-50 mt-0.5" style={{ background: '#2472C8' }} />
                </div>
                {/* Text */}
                <div>
                  <p className="text-white font-bold text-xs leading-tight mb-1">{f.title}</p>
                  <p className="text-[10px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.50)' }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ ZONE 3 — BOTTOM: stakeholder badges + copyright ══ */}
        <div className="relative z-10 space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {STAKEHOLDERS.map(s => (
              <span key={s}
                className="px-2.5 py-0.5 rounded-full text-xs bg-white/[0.07] border border-white/[0.12] text-white/55 font-medium">
                {s}
              </span>
            ))}
          </div>
          <p className="text-white font-semibold text-xs">
            © 2026 Avion Software Technologies India. All rights reserved.
          </p>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          RIGHT PANEL — original solar animation + login form
          ════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 relative flex items-center justify-center p-6 overflow-hidden bg-[#0b1a2e]">

        <SolarAnimation />

        {/* Avion logo — top-right corner of login panel */}
        <div className="absolute top-5 right-6 z-20">
          <AvionLogo width={110} variant="dark" />
        </div>

        {/* Readability gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1a2e]/25 via-transparent to-[#0b1a2e]/55 pointer-events-none" />

        <div className="relative z-10 w-full max-w-sm">

          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center gap-3 mb-8">
            <AvionLogo width={90} variant="dark" />
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded bg-accent flex items-center justify-center">
                <Sun size={14} className="text-white" />
              </div>
              <span className="text-white font-bold text-base">SMART™ Platform</span>
              <span className="text-white/45 text-xs">by</span>
              <span className="text-accent font-bold text-sm">Avion</span>
            </div>
          </div>

          {/* Glass login card */}
          <div className="rounded-2xl overflow-hidden shadow-2xl"
            style={{
              background:     'rgba(255,255,255,0.05)',
              backdropFilter: 'blur(22px)',
              border:         '1px solid rgba(255,255,255,0.11)',
            }}>
            <div className="h-[3px] w-full bg-gradient-to-r from-navy via-accent to-blue-400" />

            <div className="p-8">
              <div className="mb-6">
                <div className="flex items-baseline gap-1.5 mb-1">
                  <span className="text-xl font-extrabold text-white">SMART™</span>
                  <span className="text-sm font-medium text-white/45">Platform by</span>
                  <span className="text-sm font-extrabold text-accent">Avion</span>
                </div>
                <p className="text-xs text-white/35">
                  {loginStep === 'credentials' ? 'Sign in to your account' : 'Enter your authenticator code'}
                </p>
              </div>

              {loginStep === 'credentials' ? (
                <form onSubmit={handleCredentials} className="space-y-4">
                  {error && (
                    <div className="bg-red-900/40 border border-red-500/30 rounded-lg px-4 py-3 text-xs text-red-300">{error}</div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-white/55 mb-1.5">Username</label>
                    <input type="text" value={username} onChange={e => setUsername(e.target.value)}
                      placeholder="Enter username"
                      className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all"
                      style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
                      autoComplete="username" autoFocus required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-white/55 mb-1.5">Password</label>
                    <div className="relative">
                      <input type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                        placeholder="Enter password"
                        className="w-full px-3 py-2.5 pr-10 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all"
                        style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
                        autoComplete="current-password" required />
                      <button type="button" onClick={() => setShowPwd(p => !p)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/35 hover:text-white/65">
                        {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </div>
                  <button type="submit" disabled={loading}
                    className="w-full py-2.5 bg-accent hover:bg-accent/90 disabled:opacity-50 text-white text-sm font-bold rounded-xl transition-all shadow-lg shadow-accent/25 mt-1">
                    {loading ? 'Signing in…' : 'Sign In →'}
                  </button>
                </form>
              ) : (
                <div className="space-y-5">
                  {error && (
                    <div className="bg-red-900/40 border border-red-500/30 rounded-lg px-4 py-3 text-xs text-red-300">{error}</div>
                  )}
                  <p className="text-xs text-white/45 text-center">
                    Open your authenticator app and enter the 6-digit code
                  </p>
                  <div className="flex gap-2 justify-center" onPaste={handleTotpPaste}>
                    {totp.map((digit, idx) => (
                      <input key={idx}
                        ref={el => { totpRefs.current[idx] = el; }}
                        type="text" inputMode="numeric" maxLength={1} value={digit}
                        onChange={e => handleTotpChange(idx, e.target.value)}
                        onKeyDown={e => handleTotpKeyDown(idx, e)}
                        className={`w-11 h-12 text-center text-lg font-bold rounded-xl border-2 transition-all focus:outline-none
                          ${digit ? 'border-accent text-accent bg-accent/10' : 'text-white focus:border-accent'}`}
                        style={!digit ? { background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.15)' } : {}}
                      />
                    ))}
                  </div>
                  {loading && <p className="text-center text-xs text-white/35 animate-pulse">Verifying…</p>}
                  <button type="button"
                    onClick={() => { setLoginStep('credentials'); setError(''); setTotp(['','','','','','']); }}
                    className="w-full py-2 text-xs text-white/35 hover:text-white/60 transition-colors">
                    ← Back to login
                  </button>
                </div>
              )}
            </div>
          </div>

          <p className="text-center text-xs text-white/20 mt-5">
            © 2026 Avion Software Technologies India
          </p>
        </div>
      </div>
    </div>
  );
}
